import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import SaveIcon from '@mui/icons-material/Save'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import { restaurantDistanceBands, saveDistanceBands, setDeliveryFeeMode } from '../../apollo'
import { DZ } from '../../views/deliveryZonesTheme'

const GET_BANDS = gql`${restaurantDistanceBands}`
const SAVE_BANDS = gql`${saveDistanceBands}`
const SET_MODE = gql`${setDeliveryFeeMode}`

// ---- Presentation (the Delivery page's card: header, body, footer) ----
const PAD_X = { xs: 2.5, sm: 4 }
const CARD_TITLE = { fontSize: 20, fontWeight: 700, lineHeight: 1.3, color: DZ.text }
const BODY = { fontSize: 15, lineHeight: 1.5, color: DZ.body, '& strong': { fontWeight: 700, color: DZ.text } }
// 56px range prefix | "Up to (mi)" | "Fee" | 44px delete. The same template on
// the heading row and every tier row keeps the columns lined up.
const TIER_GRID = {
  display: 'grid',
  gridTemplateColumns: { xs: '44px minmax(0, 1fr) minmax(0, 1fr) 44px', sm: '56px minmax(0, 190px) minmax(0, 190px) 44px' },
  columnGap: { xs: 1, sm: 1.5 },
  alignItems: 'center'
}
const COLUMN_HEAD = { fontSize: 13, fontWeight: 600, lineHeight: 1.4, color: DZ.smallLabel }
const SR_ONLY = {
  position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px',
  overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0
}
// Each field keeps its label for screen readers; the column heading shows it once.
const TIER_FIELD = {
  width: '100%',
  '& .MuiInputLabel-root': SR_ONLY,
  '& .MuiOutlinedInput-input': { fontSize: { xs: 16, sm: 17 }, fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  // Phones: tighter side padding so a value like 2.99 still fits beside its "$" / "mi".
  '& .MuiInputBase-adornedStart': { pl: { xs: '10px', sm: '14px' } },
  '& .MuiInputBase-adornedEnd': { pr: { xs: '10px', sm: '14px' } },
  '& .MuiInputBase-inputAdornedStart': { pr: { xs: '8px', sm: '14px' } },
  '& .MuiInputBase-inputAdornedEnd': { pl: { xs: '10px', sm: '14px' } }
}
const GHOST_BTN = {
  width: 44, height: 44, borderRadius: '10px', color: DZ.secondary, bgcolor: 'transparent',
  '&:hover': { bgcolor: DZ.ghostHover, color: DZ.danger },
  '& .MuiSvgIcon-root': { fontSize: 20 }
}
const DASHED_BTN = {
  height: 44, borderRadius: '10px', px: 2, bgcolor: DZ.card, color: DZ.text, fontWeight: 700,
  border: `1px dashed ${DZ.dashed}`,
  '&:hover': { bgcolor: DZ.page, border: `1px dashed ${DZ.text}` }
}
const NOTE = {
  flex: '1 1 300px', minWidth: 0, alignItems: 'flex-start',
  bgcolor: DZ.noteBg, border: `1px solid ${DZ.noteBorder}`, borderRadius: '14px', p: '20px 22px',
  color: DZ.label, fontSize: 14, lineHeight: 1.55,
  '& .MuiAlert-message': { p: 0, width: '100%', overflow: 'visible' },
  '& strong': { fontWeight: 700, color: DZ.text }
}

/**
 * DistanceBandEditor — edit a restaurant's driving-distance fee tiers.
 *
 * Each band is "up to {maxMiles} mi → ${fee}". A customer is charged the first
 * band whose maxMiles covers the actual driving distance from the restaurant;
 * beyond the largest band, delivery is unavailable. Saving also flips the
 * restaurant's deliveryFeeMode to 'DISTANCE' so the new pricing takes effect.
 *
 * Props:
 *   restaurantId  string
 *   onSaved       () => void   — called after a successful save (e.g. refetch summary)
 */
export default function DistanceBandEditor({ restaurantId, onSaved }) {
  const [bands, setBands] = useState([])
  const [successMsg, setSuccessMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  const { loading } = useQuery(GET_BANDS, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const loaded = (data?.restaurantDistanceBands || []).map(b => ({
        maxMiles: String(b.maxMiles),
        fee: String(b.fee),
      }))
      setBands(loaded)
    },
  })

  const [mutSetMode] = useMutation(SET_MODE)
  const [mutSaveBands, { loading: saving }] = useMutation(SAVE_BANDS, {
    onCompleted: () => {
      setSuccessMsg('Distance pricing saved successfully.')
      setErrorMsg('')
      if (onSaved) onSaved()
      setTimeout(() => setSuccessMsg(''), 4000)
    },
    onError: (err) => { setErrorMsg(err.message || 'Failed to save distance bands'); setSuccessMsg('') },
  })

  const addBand = () => {
    const lastMax = bands.length ? parseFloat(bands[bands.length - 1].maxMiles) || 0 : 0
    setBands(prev => [...prev, { maxMiles: String(lastMax + 2), fee: '0' }])
  }
  const removeBand = (i) => setBands(prev => prev.filter((_, idx) => idx !== i))
  const updateBand = (i, key, val) => setBands(prev => prev.map((b, idx) => (idx === i ? { ...b, [key]: val } : b)))

  const handleSave = async () => {
    const parsed = bands.map(b => ({ maxMiles: parseFloat(b.maxMiles), fee: parseFloat(b.fee) }))
    for (const [i, b] of parsed.entries()) {
      if (isNaN(b.maxMiles) || b.maxMiles <= 0) { setErrorMsg(`Tier ${i + 1}: enter a valid distance in miles.`); return }
      if (isNaN(b.fee) || b.fee < 0) { setErrorMsg(`Tier ${i + 1}: enter a valid fee.`); return }
    }
    // Reject duplicate upper bounds — they'd make a tier unreachable.
    const maxSet = new Set(parsed.map(b => b.maxMiles))
    if (maxSet.size !== parsed.length) { setErrorMsg('Each tier needs a distinct "up to" distance.'); return }
    setErrorMsg('')

    const sorted = [...parsed].sort((a, b) => a.maxMiles - b.maxMiles)
    try {
      await mutSetMode({ variables: { restaurantId, mode: 'DISTANCE' } })
      await mutSaveBands({
        variables: {
          restaurantId,
          bands: sorted.map((b, idx) => ({ maxMiles: b.maxMiles, fee: b.fee, sortOrder: idx })),
        },
      })
    } catch (e) {
      // onError handles surfacing the message
    }
  }

  // Lower bound of a tier (for the "X–Y mi" label) = previous tier's maxMiles.
  const sortedForLabels = [...bands].sort((a, b) => (parseFloat(a.maxMiles) || 0) - (parseFloat(b.maxMiles) || 0))
  const lowerBoundFor = (maxMiles) => {
    const m = parseFloat(maxMiles)
    let lower = 0
    for (const b of sortedForLabels) {
      const bm = parseFloat(b.maxMiles)
      if (bm < m) lower = bm
    }
    return lower
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ px: PAD_X, pt: { xs: 3, sm: 3.5 } }}>
        <Typography component="h2" sx={CARD_TITLE}>Distance-based pricing</Typography>
        <Typography sx={{ ...BODY, mt: 0.75, maxWidth: 760 }}>
          Fees are chosen by the <strong>actual driving distance</strong> from the restaurant to the
          customer — they’re charged the first tier whose distance covers them.
        </Typography>

        {successMsg && <Alert severity="success" sx={{ mt: 2.5 }}>{successMsg}</Alert>}
        {errorMsg && <Alert severity="error" sx={{ mt: 2.5 }}>{errorMsg}</Alert>}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={24} /></Box>
      ) : (
        <>
          {/* Body: tiers left, "Good to know" right; wraps on narrow screens */}
          <Box sx={{ px: PAD_X, py: { xs: 3, sm: 3.5 }, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', columnGap: 5, rowGap: 3.5 }}>
            <Box sx={{ flex: '0 1 520px', minWidth: 0 }}>
              {bands.length === 0 && (
                <Typography sx={{ fontSize: 14, color: DZ.secondary, mb: 2 }}>
                  No tiers yet. Add one to start (e.g. up to 2 mi → $2.99).
                </Typography>
              )}

              {bands.length > 0 && (
                <Box aria-hidden sx={{ ...TIER_GRID, mb: 1 }}>
                  <span />
                  <Typography sx={COLUMN_HEAD}>Up to (mi)</Typography>
                  <Typography sx={COLUMN_HEAD}>Fee</Typography>
                  <span />
                </Box>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {bands.map((b, i) => (
                  <Box key={i} sx={TIER_GRID}>
                    <Typography sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 700, lineHeight: 1, color: DZ.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {lowerBoundFor(b.maxMiles)}–
                    </Typography>
                    <TextField
                      label="Up to (mi)"
                      value={b.maxMiles}
                      onChange={e => updateBand(i, 'maxMiles', e.target.value)}
                      size="small"
                      type="number"
                      inputProps={{ min: 0.1, step: 0.5 }}
                      InputProps={{ endAdornment: <InputAdornment position="end">mi</InputAdornment> }}
                      sx={TIER_FIELD}
                    />
                    <TextField
                      label="Fee"
                      value={b.fee}
                      onChange={e => updateBand(i, 'fee', e.target.value)}
                      size="small"
                      type="number"
                      inputProps={{ min: 0, step: 0.01 }}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      sx={TIER_FIELD}
                    />
                    <Tooltip title="Remove tier">
                      <IconButton size="small" onClick={() => removeBand(i)} sx={GHOST_BTN}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>

              <Button
                size="small"
                startIcon={<AddRoundedIcon />}
                onClick={addBand}
                variant="outlined"
                sx={{ ...DASHED_BTN, mt: bands.length ? 1.75 : 0 }}
              >
                Add tier
              </Button>
            </Box>

            <Alert severity="info" icon={false} sx={NOTE}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, fontSize: 15 }}>
                <InfoOutlinedIcon aria-hidden sx={{ fontSize: 20, color: DZ.noteIcon }} />
                <strong>Good to know</strong>
              </Box>
              <Box component="ul" sx={{ pl: 2.5, m: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <li>The farthest tier’s price also applies <strong>past its limit</strong> — e.g. with only “up to 5 mi = $5.99”, a customer at 7 mi still pays $5.99.</li>
                <li>Your <strong>Delivery boundary</strong> decides how far you actually deliver; customers outside it can’t order. Add more tiers if you want farther customers to pay more.</li>
              </Box>
            </Alert>
          </Box>

          {/* Footer */}
          <Box sx={{ borderTop: `1px solid ${DZ.border}`, px: PAD_X, py: '18px', display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || bands.length === 0}
            >
              {saving ? 'Saving…' : 'Save distance pricing'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  )
}
